import React, { useEffect, useRef, useState } from "react";
import { Award } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * TrustScoreExplainer — a 30-second animated "video" that dissects the
 * 100-point Trust Score live in the browser, section by section. Loops
 * indefinitely with a synchronized ticker on the right.
 *
 * We build this as CSS-driven bars + a JS ticker (setInterval) rather
 * than an mp4 so:
 *   - Zero asset weight (page stays fast)
 *   - Auto-plays everywhere including iOS (no muted-autoplay quirks)
 *   - Colors + copy stay in sync with the actual algorithm
 *
 * Component sequence — total 30s, matches trustScore() weights:
 *   0-4s   : Battery health (SoH)     35 pts
 *   4-8s   : Degradation velocity     15 pts
 *   8-12s  : Charging quality         10 pts
 *   12-16s : Thermal stress           10 pts
 *   16-19s : Software verified         5 pts
 *   19-22s : Fleet rank                5 pts
 *   22-25s : Warranty headroom         5 pts
 *   25-27s : No rapid degradation      2 pts
 *   27-30s : Total reveal → 97 · A+
 */
const SEQUENCE = [
  { key: "soh",        label: "Battery Health (SoH)",       max: 35, awarded: 33, tone: "#39FF14", start: 0,  end: 4  },
  { key: "velocity",   label: "Degradation Velocity",       max: 15, awarded: 14, tone: "#39FF14", start: 4,  end: 8  },
  { key: "charging",   label: "Charging Quality",           max: 10, awarded: 9,  tone: "#7FD1FF", start: 8,  end: 12 },
  { key: "thermal",    label: "Thermal Stress",             max: 10, awarded: 9,  tone: "#7FD1FF", start: 12, end: 16 },
  { key: "software",   label: "Software Verified",          max: 5,  awarded: 5,  tone: "#00E5FF", start: 16, end: 19 },
  { key: "fleet_rank", label: "Fleet Rank",                 max: 5,  awarded: 5,  tone: "#00E5FF", start: 19, end: 22 },
  { key: "warranty",   label: "Warranty Headroom",          max: 5,  awarded: 4,  tone: "#00E5FF", start: 22, end: 25 },
  { key: "not_rapid",  label: "No Rapid Degradation",       max: 2,  awarded: 2,  tone: "#39FF14", start: 25, end: 27 },
];
const LOOP_SECONDS = 30;
const TICK_MS = 50;

export const TrustScoreExplainer = () => {
  const [t, setT] = useState(0); // seconds
  const raf = useRef(null);
  const startedAt = useRef(null);

  useEffect(() => {
    const step = (ts) => {
      if (startedAt.current == null) startedAt.current = ts;
      const seconds = ((ts - startedAt.current) / 1000) % LOOP_SECONDS;
      setT(seconds);
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, []);

  // How many points are currently accumulated based on time-through-loop.
  const totalRevealed = SEQUENCE.reduce((sum, row) => {
    if (t < row.start) return sum;
    const inRow = Math.min(1, (t - row.start) / Math.max(0.001, row.end - row.start));
    return sum + row.awarded * inRow;
  }, 0);
  const showFinale = t >= 27;
  const currentPoints = Math.round(totalRevealed);

  return (
    <section
      id="trust-score-explainer"
      className="relative py-20 sm:py-28 border-t border-white/5"
      style={{ background: "#050505" }}
      data-testid="trust-score-explainer"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="mb-12 max-w-2xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-3 inline-flex items-center gap-2">
            <Award className="w-3 h-3" style={{ color: "#39FF14" }} strokeWidth={2.4} />
            How the Trust Score works
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-white font-semibold leading-tight">
            100 points. <span style={{
              background: "linear-gradient(135deg,#39FF14,#00E5FF)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>Deterministic.</span>
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed mt-4 max-w-xl">
            Every score starts from your degradation slope and warranty headroom
            (computed into the EVIQ Trust Score™) — the same 8 measurable signals,
            weighted by how much they predict real-world resale value.
            Same inputs → same score, every time. Watch it build:
          </p>
          <div
            className="mt-5 rounded-xl border px-4 py-3 max-w-xl"
            style={{ borderColor: "rgba(57,255,20,0.25)", background: "rgba(57,255,20,0.04)" }}
            data-testid="flash-test-callout"
          >
            <div className="text-[13px] text-white font-semibold">Not a flash check.</div>
            <p className="text-[12px] text-zinc-400 leading-relaxed mt-1">
              EVIQ tracks your pack&rsquo;s degradation slope over months against fleet
              median, not a one-scan estimate — and every input links back to raw telemetry.
            </p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr,320px] items-start">
          {/* LEFT: the running breakdown */}
          <div
            className="rounded-2xl border p-6 sm:p-8"
            style={{ borderColor: "rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.015)" }}
          >
            <div className="space-y-3.5">
              {SEQUENCE.map((row) => {
                const active = t >= row.start && t < row.end;
                const done = t >= row.end;
                const progressPct = t < row.start
                  ? 0
                  : t >= row.end
                    ? 100
                    : ((t - row.start) / (row.end - row.start)) * 100;
                const shownAwarded = Math.round(row.awarded * (progressPct / 100));
                return (
                  <div
                    key={row.key}
                    className="transition-opacity duration-300"
                    style={{ opacity: t < row.start ? 0.35 : 1 }}
                    data-testid={`trust-explainer-row-${row.key}`}
                  >
                    <div className="flex items-baseline justify-between mb-1.5">
                      <div
                        className="text-[13px] leading-none transition-colors duration-300"
                        style={{ color: active ? row.tone : done ? "#ffffff" : "#a1a1aa" }}
                      >
                        {row.label}
                      </div>
                      <div className="font-mono text-[11px]">
                        <span
                          className="transition-colors"
                          style={{ color: active || done ? row.tone : "#71717a" }}
                        >
                          {shownAwarded}
                        </span>
                        <span className="text-zinc-600"> / {row.max}</span>
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-white/[0.05] overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${progressPct}%`,
                          background: row.tone,
                          boxShadow: active ? `0 0 12px ${row.tone}80` : "none",
                          transition: "width 0.05s linear, box-shadow 0.3s ease",
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 pt-4 border-t border-white/5 flex items-baseline justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                Running total
              </div>
              <div
                className="font-display text-2xl font-semibold transition-colors"
                style={{ color: showFinale ? "#39FF14" : "#ffffff" }}
                data-testid="trust-explainer-running-total"
              >
                {currentPoints} / 100
              </div>
            </div>
          </div>

          {/* RIGHT: finale reveal */}
          <div
            className="rounded-2xl border p-6 sm:p-8 transition-all duration-500 relative overflow-hidden"
            style={{
              borderColor: showFinale ? "rgba(57,255,20,0.55)" : "rgba(255,255,255,0.10)",
              background: showFinale
                ? "linear-gradient(135deg, rgba(57,255,20,0.16) 0%, rgba(0,229,255,0.06) 100%)"
                : "rgba(255,255,255,0.015)",
              boxShadow: showFinale ? "0 8px 60px rgba(57,255,20,0.25)" : "none",
            }}
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              EVIQ Trust Score™
            </div>
            <div className="flex items-baseline gap-2 mt-2">
              <span
                className="font-display text-6xl font-semibold leading-none transition-colors duration-500"
                style={{ color: showFinale ? "#39FF14" : "#ffffff" }}
              >
                {showFinale ? 97 : currentPoints}
              </span>
              <span className="font-mono text-[11px] text-zinc-500">/ 100</span>
            </div>
            <div
              className="mt-4 pt-4 border-t border-white/5 transition-opacity duration-500"
              style={{ opacity: showFinale ? 1 : 0.4 }}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                EVIQ Health Grade™
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Award className="w-6 h-6" style={{ color: showFinale ? "#39FF14" : "#71717a" }} strokeWidth={2.4} />
                <span
                  className="font-display text-5xl font-semibold leading-none transition-colors duration-500"
                  style={{ color: showFinale ? "#39FF14" : "#71717a" }}
                >
                  A+
                </span>
              </div>
            </div>
            <div className="mt-6 text-[11px] text-zinc-400 leading-relaxed">
              Sustained above 95 for 90 days? Your pack earns a{" "}
              <span className="text-white font-semibold">public, buyer-facing preservation badge</span>{" "}
              — proof of a flat degradation slope, top fleet rank, and wide warranty headroom.
            </div>
            <Link
              to="/legendary"
              className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider text-white hover:bg-white/[0.06] transition-colors"
              data-testid="trust-explainer-legendary-cta"
            >
              See the Legends
              →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
